import { getFunctions, httpsCallable } from "firebase/functions";

// Toggle between test and live mode
// Set to true for test mode, false for live mode
const RAZORPAY_TEST_MODE = false;

/**
 * Log payment status (cancelled/failed) to Firebase
 */
const logPaymentStatus = async (data) => {
  try {
    const functions = getFunctions();
    const logStatus = httpsCallable(functions, "logRazorpayPaymentStatus");
    await logStatus(data);
  } catch (error) {
    console.error("Error logging payment status:", error);
  }
};

// Razorpay Configuration
const RAZORPAY_CONFIG = {
  keyId: RAZORPAY_TEST_MODE
    ? "rzp_test_S5PEVQTvUJOVht"  // Test key
    : "rzp_live_S4fMnzzz30NARv", // Live key
  currency: "INR",
  name: "Jurident",
  description: "Subscription Payment",
  image: "/logo.png",
  theme: {
    color: "#050125",
  },
};

/**
 * Generate a unique receipt ID
 */
const generateReceiptId = () => {
  return `rcpt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create Razorpay order via Firebase Function
 * @param {number} amount - Amount (will be converted to smallest currency unit)
 * @param {string} userId - User ID
 * @param {object} planDetails - Plan details including category, duration, planType
 * @param {string} currency - Currency code (INR, USD, etc.)
 * @returns {Promise<object>} Order details
 */
export const createRazorpayOrder = async (amount, userId, planDetails, currency = "INR") => {
  try {
    const functions = getFunctions();
    const createOrder = httpsCallable(functions, "createRazorpayOrder");

    // Convert to smallest currency unit (paise for INR, cents for USD)
    const amountInSmallestUnit = Math.round(amount * 100);

    const result = await createOrder({
      amount: amountInSmallestUnit,
      currency: currency,
      receipt: generateReceiptId(),
      userId,
      planDetails,
    });

    return {
      success: true,
      orderId: result.data.orderId,
      amount: result.data.amount,
      currency: result.data.currency,
    };
  } catch (error) {
    console.error("Error creating Razorpay order:", error);
    return {
      success: false,
      error: error.message || "Failed to create order",
    };
  }
};

/**
 * Open Razorpay checkout and process payment
 * @param {object} params - Payment parameters
 * @returns {Promise<object>} Payment result
 */
export const initiateRazorpayPayment = (params) => {
  const {
    orderId,
    amount,
    currency,
    userId,
    userEmail,
    userName,
    userPhone,
    planDetails,
    onSuccess,
    onFailure,
  } = params;

  return new Promise((resolve, reject) => {
    const options = {
      key: RAZORPAY_CONFIG.keyId,
      amount: amount,
      currency: currency || RAZORPAY_CONFIG.currency,
      name: RAZORPAY_CONFIG.name,
      description: `${planDetails.category} Subscription`,
      image: RAZORPAY_CONFIG.image,
      order_id: orderId,
      handler: function (response) {
        // Payment successful
        const paymentData = {
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
          userId,
          planDetails,
          amount: amount / 100, // Convert back to INR
        };

        if (onSuccess) onSuccess(paymentData);
        resolve({
          success: true,
          ...paymentData,
        });
      },
      prefill: {
        name: userName || "",
        email: userEmail || "",
        contact: userPhone || "",
      },
      notes: {
        userId: userId,
        planCategory: planDetails.category,
        planType: planDetails.planType,
      },
      theme: RAZORPAY_CONFIG.theme,
      modal: {
        ondismiss: function () {
          // Log cancelled payment
          logPaymentStatus({
            orderId: orderId,
            userId: userId,
            status: "cancelled",
            errorMessage: "Payment cancelled by user",
            planDetails: planDetails,
            amount: amount,
            currency: currency || RAZORPAY_CONFIG.currency,
          });
          if (onFailure) onFailure({ error: "Payment cancelled by user" });
          resolve({
            success: false,
            error: "Payment cancelled by user",
          });
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        const errorData = {
          code: response.error.code,
          description: response.error.description,
          source: response.error.source,
          step: response.error.step,
          reason: response.error.reason,
          orderId: response.error.metadata?.order_id,
          paymentId: response.error.metadata?.payment_id,
        };

        // Log failed payment
        logPaymentStatus({
          orderId: orderId,
          userId: userId,
          status: "failed",
          errorMessage: response.error.description || "Payment failed",
          errorDetails: errorData,
          planDetails: planDetails,
          amount: amount,
          currency: currency || RAZORPAY_CONFIG.currency,
        });

        if (onFailure) onFailure(errorData);
        resolve({
          success: false,
          error: response.error.description || "Payment failed",
          errorDetails: errorData,
        });
      });

      rzp.open();
    } catch (error) {
      console.error("Error opening Razorpay checkout:", error);
      if (onFailure) onFailure({ error: error.message });
      reject({
        success: false,
        error: error.message || "Failed to open payment gateway",
      });
    }
  });
};

/**
 * Verify Razorpay payment via Firebase Function
 * @param {object} paymentData - Payment data from Razorpay
 * @returns {Promise<object>} Verification result
 */
export const verifyRazorpayPayment = async (paymentData) => {
  try {
    const functions = getFunctions();
    const verifyPayment = httpsCallable(functions, "verifyRazorpayPayment");

    const result = await verifyPayment({
      razorpay_payment_id: paymentData.razorpay_payment_id,
      razorpay_order_id: paymentData.razorpay_order_id,
      razorpay_signature: paymentData.razorpay_signature,
      userId: paymentData.userId,
      planDetails: paymentData.planDetails,
      amount: paymentData.amount,
    });

    return {
      success: result.data.success,
      message: result.data.message,
      transactionId: result.data.transactionId,
    };
  } catch (error) {
    console.error("Error verifying Razorpay payment:", error);
    return {
      success: false,
      error: error.message || "Payment verification failed",
    };
  }
};

/**
 * Complete Razorpay payment flow
 * @param {object} params - Payment parameters
 * @returns {Promise<object>} Final payment result
 */
export const processRazorpayPayment = async (params) => {
  const { amount, currency = "INR", userId, userEmail, userName, userPhone, planDetails } = params;

  try {
    // Step 1: Create order with specified currency
    const orderResult = await createRazorpayOrder(amount, userId, planDetails, currency);

    if (!orderResult.success) {
      return {
        success: false,
        error: orderResult.error || "Failed to create order",
      };
    }

    // Step 2: Open checkout and get payment
    const paymentResult = await initiateRazorpayPayment({
      orderId: orderResult.orderId,
      amount: orderResult.amount,
      currency: orderResult.currency,
      userId,
      userEmail,
      userName,
      userPhone,
      planDetails,
    });

    if (!paymentResult.success) {
      return {
        success: false,
        error: paymentResult.error || "Payment failed",
      };
    }

    // Step 3: Verify payment and save subscription
    const verificationResult = await verifyRazorpayPayment(paymentResult);

    return {
      success: verificationResult.success,
      message: verificationResult.message,
      transactionId: verificationResult.transactionId,
      error: verificationResult.error,
    };
  } catch (error) {
    console.error("Razorpay payment flow error:", error);
    return {
      success: false,
      error: error.message || "Payment processing failed",
    };
  }
};

export default {
  createRazorpayOrder,
  initiateRazorpayPayment,
  verifyRazorpayPayment,
  processRazorpayPayment,
};
